import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";

const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const { count, wishlist, setDrawerOpen } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `text-[11px] tracking-[0.15em] uppercase font-medium link-underline transition-colors whitespace-nowrap ${
      isActive ? "text-[#B34A36]" : "text-[#1A1A1A] hover:text-[#B34A36]"
    }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF9F6]/90 border-b border-[#E5E1D8]"
          : "bg-[#FAF9F6]/70 border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="px-6 md:px-10 lg:px-16 h-20 md:h-24 flex items-center justify-between gap-6">
        {/* Logo — left aligned, watercolor marigold + large editorial wordmark */}
        <Link
          to="/"
          className="flex items-center gap-3 md:gap-4 leading-none shrink-0"
          data-testid="logo-link"
        >
          <img
            src="/marigold-logo.png"
            alt="Maison Marigold"
            className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 object-contain select-none shrink-0"
            draggable={false}
          />
          <div className="flex flex-col items-start">
            <span className="font-serif-display font-medium tracking-[0.02em] text-[#1E2B3C] whitespace-nowrap text-[1.55rem] sm:text-[1.85rem] md:text-[2.2rem] lg:text-[2.5rem] leading-[1]">
              Maison <span className="italic text-[#B34A36]">Marigold</span>
            </span>
            <span className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[#5C5C5C] mt-2">
              Souffle de l'Inde · Paris
            </span>
          </div>
        </Link>

        {/* Right cluster: nav + actions */}
        <div className="flex items-center gap-5 lg:gap-8">
          <nav className="hidden md:flex items-center gap-3 lg:gap-5 flex-nowrap" data-testid="nav-left">
            <NavLink to="/spices" className={linkClass} data-testid="nav-spices">
              {t.nav.spices}
            </NavLink>
            <NavLink to="/saffron" className={linkClass} data-testid="nav-saffron">
              {t.nav.saffron}
            </NavLink>
            <NavLink to="/textiles" className={linkClass} data-testid="nav-textiles">
              {t.nav.textiles}
            </NavLink>
            <NavLink to="/deco" className={linkClass} data-testid="nav-deco">
              {t.nav.deco}
            </NavLink>
            <NavLink to="/oils" className={linkClass} data-testid="nav-oils">
              {t.nav.oils}
            </NavLink>
            <span className="w-px h-4 bg-[#E5E1D8] mx-1" />
            <NavLink to="/story" className={linkClass} data-testid="nav-story">
              {t.nav.story}
            </NavLink>
            <NavLink to="/artisans" className={linkClass} data-testid="nav-artisans">
              {t.nav.artisans}
            </NavLink>
          </nav>

          <div className="hidden md:block w-px h-5 bg-[#E5E1D8]" />

          {/* Actions */}
          <div className="flex items-center gap-4 md:gap-5">
          <button
            onClick={toggle}
            className="text-xs tracking-[0.18em] uppercase font-medium hidden md:flex items-center gap-1 text-[#1A1A1A] hover:text-[#B34A36] transition-colors"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <span className={lang === "fr" ? "text-[#B34A36]" : ""}>FR</span>
            <span className="text-[#E5E1D8]">/</span>
            <span className={lang === "en" ? "text-[#B34A36]" : ""}>EN</span>
          </button>

          <Link
            to="/wishlist"
            className="relative text-[#1A1A1A] hover:text-[#B34A36] transition-colors"
            data-testid="nav-wishlist"
            aria-label="Wishlist"
          >
            <Heart size={20} strokeWidth={1.5} />
            {wishlist.length > 0 && (
              <span
                className="absolute -top-1 -right-2 text-[10px] font-medium text-white bg-[#B34A36] rounded-full w-4 h-4 flex items-center justify-center"
                data-testid="wishlist-count"
              >
                {wishlist.length}
              </span>
            )}
          </Link>

          <button
            onClick={() => setDrawerOpen(true)}
            className="relative text-[#1A1A1A] hover:text-[#B34A36] transition-colors"
            data-testid="nav-cart"
            aria-label="Cart"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span
                className="absolute -top-1 -right-2 text-[10px] font-medium text-white bg-[#1E2B3C] rounded-full w-4 h-4 flex items-center justify-center"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>

          <button
            className="md:hidden text-[#1A1A1A]"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
        </div>
      </div>

      {/* Mobile universe strip — horizontal scrollable, always visible below logo */}
      <nav
        className="md:hidden border-t border-[#E5E1D8] bg-[#FAF9F6]/95 backdrop-blur-xl overflow-x-auto"
        data-testid="mobile-universe-strip"
      >
        <div className="flex items-center gap-5 px-6 py-3 whitespace-nowrap w-max">
          <NavLink to="/spices" className={linkClass} data-testid="ms-nav-spices">{t.nav.spices}</NavLink>
          <NavLink to="/saffron" className={linkClass} data-testid="ms-nav-saffron">{t.nav.saffron}</NavLink>
          <NavLink to="/textiles" className={linkClass} data-testid="ms-nav-textiles">{t.nav.textiles}</NavLink>
          <NavLink to="/deco" className={linkClass} data-testid="ms-nav-deco">{t.nav.deco}</NavLink>
          <NavLink to="/oils" className={linkClass} data-testid="ms-nav-oils">{t.nav.oils}</NavLink>
        </div>
      </nav>

      {/* Mobile drawer — secondary nav (Story, Artisans, Lang) */}
      {open && (
        <div
          className="md:hidden border-t border-[#E5E1D8] bg-[#FAF9F6] px-6 py-6 flex flex-col gap-5"
          data-testid="mobile-menu"
        >
          <NavLink to="/story" className={linkClass} data-testid="m-nav-story">{t.nav.story}</NavLink>
          <NavLink to="/artisans" className={linkClass} data-testid="m-nav-artisans">
            {t.nav.artisans}
          </NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="m-nav-contact">
            {t.nav.contact}
          </NavLink>
          <button
            onClick={toggle}
            className="text-xs tracking-[0.18em] uppercase font-medium text-left text-[#B34A36]"
            data-testid="m-lang-toggle"
          >
            {lang === "fr" ? "Switch to English" : "Passer en français"}
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
